import { createTheme } from '@mui/material';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';

// Pull the active client's palette
const light = clientThemePalette[CLIENT_THEME].light;
const dark = clientThemePalette[CLIENT_THEME].dark;

declare module '@mui/material/styles' {
  interface Palette {
    customBackground?: {
      section: string;
      modal: string;
    };
    navbar?: {
      bg: string;
      activeBg: string;
      activeSubBg: string;
      activeColor: string;
      hoverColor: string;
    };
    topbar?: {
      bg: string;
      gradient: string | null;
    };
    breadcrumb?: {
      bg: string;
      activeColor: string;
      inactiveColor: string;
    };
    card?: {
      borderColor: string;
    };
  }
  interface PaletteOptions {
    customBackground?: {
      section: string;
      modal: string;
    };
    navbar?: {
      bg: string;
      activeBg: string;
      activeSubBg: string;
      activeColor: string;
      hoverColor: string;
    };
    topbar?: {
      bg: string;
      gradient: string | null;
    };
    breadcrumb?: {
      bg: string;
      activeColor: string;
      inactiveColor: string;
    };
    card?: {
      borderColor: string;
    };
    color?: Record<string, string>;
    borderColor?: Record<string, string>;
  }
}

// Common component styles shared between themes
const componentOverrides = {
  MuiTab: {
    styleOverrides: {
      root: {
        color: 'text.secondary',
        '&.Mui-selected': {
          color: 'primary.main',
        },
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
        '&.Mui-selected': {
          backgroundColor: light.toggleSelected,
          color: '#fff',
        },
        '&.Mui-selected:hover': {
          backgroundColor: light.toggleSelectedHover,
        },
      },
    },
  },
};

const fontFamily = 'Poppins';

// ─── Dark Theme ───────────────────────────────────────────────────────────────
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: dark.primary,
    },
    background: {
      default: dark.pageBackground,
      paper: dark.paperBackground,
    },
    customBackground: {
      section: dark.sectionBackground,
      modal: dark.modalBackground,
    },
    navbar: {
      bg: dark.navbarBg,
      activeBg: dark.navbarActiveBg,
      activeSubBg: dark.navbarActiveSubBg,
      activeColor: dark.navbarActiveColor,
      hoverColor: dark.navbarHoverColor,
    },
    topbar: {
      bg: dark.topbarBg,
      gradient: dark.topbarGradient,
    },
    breadcrumb: {
      bg: dark.breadcrumbBg,
      activeColor: dark.breadcrumbActiveColor,
      inactiveColor: dark.breadcrumbInactiveColor,
    },
    card: {
      borderColor: dark.cardBorderColor,
    },
    text: {
      primary: dark.textPrimary,
      secondary: dark.textSecondary,
    },
    svgIcons: {
      primary: { fill: dark.primary },
      error: { fill: dark.danger },
    },
    color: {
      grey: dark.grey,
      cornflowerBlue: dark.cornflowerBlue,
      mediumGray: dark.mediumGray,
      lightBlack: dark.lightDark,
      darkGrey: dark.darkGrey,
    },
    borderColor: {
      grey: dark.borderGrey,
    },
  },
  components: {
    ...componentOverrides,
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: dark.sectionBackground,
          border: 'none',
        },
      },
    },
  },
  typography: { fontFamily },
});

// ─── Light Theme ──────────────────────────────────────────────────────────────
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: light.primary,
    },
    background: {
      default: light.pageBackground,
      paper: light.paperBackground,
    },
    customBackground: {
      section: light.sectionBackground,
      modal: light.modalBackground,
    },
    navbar: {
      bg: light.navbarBg,
      activeBg: light.navbarActiveBg,
      activeSubBg: light.navbarActiveSubBg,
      activeColor: light.navbarActiveColor,
      hoverColor: light.navbarHoverColor,
    },
    topbar: {
      bg: light.topbarBg,
      gradient: light.topbarGradient,
    },
    breadcrumb: {
      bg: light.breadcrumbBg,
      activeColor: light.breadcrumbActiveColor,
      inactiveColor: light.breadcrumbInactiveColor,
    },
    card: {
      borderColor: light.cardBorderColor,
    },
    text: {
      primary: light.textPrimary,
      secondary: light.textSecondary,
    },
    svgIcons: {
      primary: { fill: light.primary },
      error: { fill: light.danger },
    },
    color: {
      grey: light.grey,
      cornflowerBlue: light.cornflowerBlue,
      mediumGray: light.mediumGray,
      lightBlack: light.lightDark,
      darkGrey: light.darkGrey,
    },
    borderColor: {
      grey: light.borderGrey,
    },
  },
  components: {
    ...componentOverrides,
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: light.toolBarBackground,
        },
      },
    },
  },
  typography: { fontFamily },
});
